import { useState, useRef, useEffect, Fragment } from 'react';
import {
  AlertTriangle,
  ArrowRight,
  CheckCircle2,
  Info,
  Loader2,
  Upload,
  XCircle,
} from 'lucide-react';
import {
  api,
  convertApiResponse,
  type ConvertedScreeningRow,
  type PortfolioPosition,
  type PortfolioSummary,
  type ScreeningRequest,
  type ScreeningResult,
} from '../../services/api';
import { ComplianceScoreHero, EtfXRayPanel } from '../PortfolioSummaryPanels';
import { PortfolioAskPanel } from '../PortfolioAskPanel';
import { colors } from '../../lib/theme';
import { LENS_LABELS } from '../../lib/lens-labels';
import {
  SCREENING_PRESETS,
  findActivePreset,
  summarizeResults,
  isNotInDatabaseReasons,
  type FilterToggles,
  type ScreeningPresetId,
} from '../../lib/screening-presets';
import { MIN_SCAN_MS, sleep, type ScreenPhase } from '../../lib/screen-phase';
import { useBackendHealth } from '../../hooks/useBackendHealth';
import { ToggleRow } from './ToggleRow';
import {
  FinalVerdict,
  OccupationStatusPill,
  StatusPill,
  SummaryPill,
} from './ScreeningStatusPills';
import { ExpandableEvidenceCell, EvidenceDetails } from './ExpandableEvidenceCell';
import { LoginModal } from './LoginModal';
import { ScanningView } from './ScanningView';

function resultsCopy(summary: { clean: number; review: number; excluded: number; total: number }) {
  if (summary.excluded > 0) {
    return {
      title: (
        <>
          Your portfolio isn&apos;t as{' '}
          <span style={{ color: colors.yellow }}>clean as it looks.</span>
        </>
      ),
      body: `${summary.clean} holding${summary.clean === 1 ? ' passes' : 's pass'}. ${summary.excluded} excluded, ${summary.review} need a look.`,
    };
  }
  if (summary.review > 0) {
    return {
      title: (
        <>
          Most of it passes.{' '}
          <span style={{ color: colors.yellow }}>A few need a look.</span>
        </>
      ),
      body: `${summary.review} holding${summary.review === 1 ? '' : 's'} flagged for review. ${summary.clean} came back clean.`,
    };
  }
  return {
    title: <>This screen came back clean.</>,
    body: `${summary.total} holding${summary.total === 1 ? '' : 's'} screened. Unknown tickers would show as Review, not clean.`,
  };
}

type ScreeningPanelProps = {
  phase: ScreenPhase;
  onPhaseChange: (phase: ScreenPhase) => void;
  newScanNonce: number;
};

export function ScreeningPanel({ phase, onPhaseChange, newScanNonce }: ScreeningPanelProps) {
  const [tickers, setTickers] = useState("");
  const [toggles, setToggles] = useState<FilterToggles>({ 
    bds: { enabled: true, categories: undefined }, 
    defense: false,
    surveillance: false,
    shariah: false 
  });
  const [results, setResults] = useState<ConvertedScreeningRow[] | null>(null);
  const [rawRows, setRawRows] = useState<ScreeningResult[] | null>(null);
  const [portfolio, setPortfolio] = useState<PortfolioSummary | null>(null);
  const [positions, setPositions] = useState<PortfolioPosition[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isParsingCsv, setIsParsingCsv] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [csvWarnings, setCsvWarnings] = useState<string[]>([]);
  const { backendStatus, checkBackendHealth } = useBackendHealth();
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [expandedRows, setExpandedRows] = useState<number[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const activePreset = findActivePreset(toggles);

  useEffect(() => {
    if (newScanNonce === 0) return;
    setResults(null);
    setRawRows(null);
    setPortfolio(null);
    setError(null);
    setCsvWarnings([]);
    setExpandedRows([]);
    setIsLoading(false);
    onPhaseChange('input');
  }, [newScanNonce, onPhaseChange]);

  function toggleRow(index: number) {
    setExpandedRows(prev => 
      prev.includes(index) 
        ? prev.filter(i => i !== index)
        : [...prev, index]
    );
  }

  async function runDemo() {
    if (!tickers.trim()) {
      setError("Please enter some ticker symbols first!");
      return;
    }

    try {
      setError(null);
      setIsLoading(true);
      setExpandedRows([]);
      onPhaseChange('scanning');
      const startedAt = Date.now();
      
      const symbols = tickers
        .split(/[,\n]/)
        .map(s => s.trim().toUpperCase())
        .filter(s => s.length > 0);

      if (symbols.length === 0) {
        setError("Please enter valid ticker symbols!");
        onPhaseChange('input');
        return;
      }

      const requestPositions: PortfolioPosition[] =
        positions.length > 0
          ? positions.filter((p) => symbols.includes(p.symbol))
          : symbols.map((s) => ({ symbol: s }));

      const request: ScreeningRequest = {
        symbols,
        positions: requestPositions,
        filters: {
          bds: toggles.bds,
          defense: toggles.defense,
          surveillance: toggles.surveillance,
          shariah: toggles.shariah,
        },
        options: {
          lookthrough: true,
          autoDiscover: false,
        },
      };

      const response = await api.runScreening(request);
      const elapsed = Date.now() - startedAt;
      if (elapsed < MIN_SCAN_MS) {
        await sleep(MIN_SCAN_MS - elapsed);
      }
      const convertedResults = convertApiResponse(response);
      setResults(convertedResults);
      setRawRows(response.rows);
      setPortfolio(response.portfolio ?? null);
      onPhaseChange('results');
      
    } catch (err) {
      console.error("Screening failed:", err);
      setError(`Screening failed: ${err instanceof Error ? err.message : 'Unknown error'}`);
      onPhaseChange('input');
    } finally {
      setIsLoading(false);
    }
  }

  function applyPreset(presetId: ScreeningPresetId) {
    const preset = SCREENING_PRESETS.find((p) => p.id === presetId);
    if (preset) setToggles(preset.toggles);
  }

  async function handleCsvUpload(file: File) {
    setCsvWarnings([]);
    setError(null);
    setExpandedRows([]);
    setIsParsingCsv(true);
    try {
      const text = await file.text();
      onPhaseChange('scanning');
      const startedAt = Date.now();
      const parsed = await api.parseHoldings(text, {
        screen: true,
        filters: {
          bds: toggles.bds,
          defense: toggles.defense,
          surveillance: toggles.surveillance,
          shariah: toggles.shariah,
        },
        options: {
          lookthrough: true,
          autoDiscover: false,
        },
      });
      if (parsed.tickers.length === 0) {
        setError("No valid tickers found in that CSV.");
        onPhaseChange('input');
        return;
      }
      setTickers(parsed.tickers.join(", "));
      setPositions(parsed.positions ?? parsed.tickers.map((s) => ({ symbol: s })));
      if (parsed.warnings?.length) setCsvWarnings(parsed.warnings);

      if (parsed.rows && parsed.asOf) {
        const elapsed = Date.now() - startedAt;
        if (elapsed < MIN_SCAN_MS) {
          await sleep(MIN_SCAN_MS - elapsed);
        }
        const convertedResults = convertApiResponse({
          requestId: parsed.requestId,
          asOf: parsed.asOf,
          rows: parsed.rows,
          portfolio: parsed.portfolio,
          warnings: parsed.warnings ?? [],
        });
        setResults(convertedResults);
        setRawRows(parsed.rows);
        setPortfolio(parsed.portfolio ?? null);
        onPhaseChange('results');
      } else {
        onPhaseChange('input');
      }
    } catch (err) {
      setError(`CSV upload failed: ${err instanceof Error ? err.message : 'Unknown error'}`);
      onPhaseChange('input');
    } finally {
      setIsParsingCsv(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }

  const summary = results ? summarizeResults(results) : null;
  const canRun = tickers.trim().length > 0 && !isLoading;
  const tickerCount = tickers.split(/[\s,\n]+/).map((s) => s.trim()).filter(Boolean).length;
  const colCount =
    5 + (toggles.bds.enabled ? 1 : 0) + (toggles.defense ? 1 : 0) + (toggles.shariah ? 1 : 0);
  const headline = summary && summary.total > 0 ? resultsCopy(summary) : null;

  if (phase === 'scanning') {
    return (
      <section className="mt-4 mb-4 pt-[80px] sm:pt-[110px]">
        <ScanningView toggles={toggles} />
      </section>
    );
  }

  if (phase === 'results') {
    return (
      <section className="mt-6 mb-8 pt-4">
        {headline && summary && (
          <div className="max-w-2xl">
            <div className="font-mono text-[12px] uppercase tracking-[1px] mb-3.5" style={{ color: colors.textMuted }}>
              Screen complete · {summary.total} holding{summary.total === 1 ? '' : 's'} screened
            </div>
            <h2 className="display text-[34px] sm:text-[50px] leading-[1.14] tracking-tight m-0 font-normal">
              {headline.title}
            </h2>
            <p className="mt-5 text-[17px] leading-relaxed" style={{ color: colors.textSecondary }}>
              {headline.body}
            </p>
            <div className="mt-5 flex flex-wrap gap-2">
              <SummaryPill label="Clean" count={summary.clean} color={colors.green} />
              <SummaryPill label="Review" count={summary.review} color={colors.yellow} />
              <SummaryPill label="Excluded" count={summary.excluded} color={colors.red} />
              {summary.notInDatabase > 0 && (
                <SummaryPill label="Not in database" count={summary.notInDatabase} color={colors.textMuted} />
              )}
            </div>
          </div>
        )}

        {portfolio && (
          <>
            <ComplianceScoreHero portfolio={portfolio} />
            <EtfXRayPanel portfolio={portfolio} />
          </>
        )}

        {rawRows && rawRows.length > 0 && results && (
          <PortfolioAskPanel
            context={{ rows: rawRows, portfolio: portfolio ?? undefined }}
            filters={{
              bds: toggles.bds,
              defense: toggles.defense,
              surveillance: toggles.surveillance,
              shariah: toggles.shariah,
            }}
            onCitationClick={(symbol) => {
              const idx = results.findIndex((r) => r.ticker === symbol);
              if (idx >= 0 && !expandedRows.includes(idx)) {
                setExpandedRows((prev) => [...prev, idx]);
              }
            }}
          />
        )}

        <div className="mt-12">
          {results && results.length > 0 && (
            <div className="mb-4 flex items-end justify-between gap-3">
              <h3 className="text-[22px] font-bold tracking-tight m-0">Holding by holding</h3>
              <span className="font-mono text-[11.5px] whitespace-nowrap" style={{ color: colors.textMuted }}>
                click any row for evidence
              </span>
            </div>
          )}
          <div
            className="overflow-x-auto rounded-2xl"
            style={{
              border: `1px solid ${colors.border}`,
              backgroundColor: colors.bgTertiary,
            }}
          >
            <table className="w-full text-sm">
              <thead className="text-left">
                <tr style={{ borderBottom: `1px solid ${colors.border}` }}>
                  <th className="py-3.5 px-4 text-[11.5px] font-semibold uppercase tracking-wide" style={{ color: colors.textMuted }}>Ticker</th>
                  <th className="py-3.5 px-4 text-[11.5px] font-semibold uppercase tracking-wide" style={{ color: colors.textMuted }}>Company</th>
                  {toggles.bds.enabled && (
                    <th className="py-3.5 px-4 text-[11.5px] font-semibold uppercase tracking-wide" style={{ color: colors.textMuted }}>{LENS_LABELS.occupationShort}</th>
                  )}
                  {toggles.defense && (
                    <th className="py-3.5 px-4 text-[11.5px] font-semibold uppercase tracking-wide" style={{ color: colors.textMuted }}>{LENS_LABELS.defenseShort}</th>
                  )}
                  {toggles.shariah && (
                    <th className="py-3.5 px-4 text-[11.5px] font-semibold uppercase tracking-wide" style={{ color: colors.textMuted }}>{LENS_LABELS.shariahShort}</th>
                  )}
                  <th className="py-3.5 px-4 text-[11.5px] font-semibold uppercase tracking-wide" style={{ color: colors.textMuted }}>Final</th>
                  <th className="py-3.5 px-4 text-[11.5px] font-semibold uppercase tracking-wide" style={{ color: colors.textMuted }}>Evidence</th>
                  <th className="py-3.5 px-4 text-[11.5px] font-semibold uppercase tracking-wide" style={{ color: colors.textMuted }}>Sources</th>
                </tr>
              </thead>
              <tbody>
                {(results ?? []).map((r, i) => {
                  const notInDb = isNotInDatabaseReasons(r.reasons);
                  const isOpen = expandedRows.includes(i);
                  return (
                    <Fragment key={i}>
                      <tr
                        className="transition-colors cursor-pointer"
                        onClick={() => toggleRow(i)}
                        style={{
                          borderBottom: isOpen ? 'none' : `1px solid ${colors.borderDark}`,
                          backgroundColor: isOpen ? colors.bgTertiary : undefined,
                          borderLeft: notInDb ? `3px solid ${colors.yellow}` : undefined,
                        }}
                      >
                        <td className="py-3.5 px-4 font-mono font-bold" style={{ color: colors.textPrimary }}>{r.ticker}</td>
                        <td className="py-3.5 px-4" style={{ color: colors.textSecondary }}>
                          <div>{r.name}</div>
                          {notInDb && (
                            <span
                              className="mt-1 inline-block text-[10px] uppercase tracking-wide rounded px-1.5 py-0.5"
                              style={{
                                backgroundColor: `${colors.yellow}20`,
                                color: colors.yellow,
                              }}
                            >
                              Not in database
                            </span>
                          )}
                        </td>
                        {toggles.bds.enabled && (
                          <td className="py-3.5 px-4"><OccupationStatusPill bds={r.bds} /></td>
                        )}
                        {toggles.defense && (
                          <td className="py-3.5 px-4"><StatusPill value={r.defense} /></td>
                        )}
                        {toggles.shariah && (
                          <td className="py-3.5 px-4">
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <StatusPill
                                value={r.shariah}
                                labelOverride={
                                  r.shariah === 'block'
                                    ? 'Non‑compliant'
                                    : r.shariah === 'review'
                                      ? 'Review'
                                      : undefined
                                }
                              />
                              {r.shariahEstimated && (
                                <span
                                  className="text-[10px] uppercase tracking-wide rounded px-1.5 py-0.5 shrink-0"
                                  style={{ backgroundColor: `${colors.yellow}20`, color: colors.yellow }}
                                  title="Shariah ratios from Yahoo Finance fallback — verify against filings"
                                >
                                  Est.
                                </span>
                              )}
                            </div>
                          </td>
                        )}
                        <td className="py-3.5 px-4"><FinalVerdict value={r.verdict} /></td>
                        <td className="py-3.5 px-4" onClick={(e) => e.stopPropagation()}>
                          <ExpandableEvidenceCell
                            company={r}
                            filters={toggles}
                            isExpanded={isOpen}
                            onToggle={() => toggleRow(i)}
                          />
                        </td>
                        <td className="py-3.5 px-4" onClick={(e) => e.stopPropagation()}>
                          <div className="flex flex-wrap gap-1.5">
                            {r.sources.map((s: { label: string; url: string }, idx: number) => {
                              const isReal = s.url && s.url !== 'source pending' && s.url.startsWith('http');
                              return isReal ? (
                                <a
                                  key={idx}
                                  href={s.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="rounded-md px-2 py-1 text-xs hover:opacity-80 transition-opacity"
                                  style={{
                                    border: `1px solid ${colors.border}`,
                                    backgroundColor: colors.bgSecondary,
                                    color: colors.textSecondary,
                                  }}
                                >
                                  {s.label}
                                </a>
                              ) : (
                                <span
                                  key={idx}
                                  className="rounded-md px-2 py-1 text-xs opacity-50 cursor-default"
                                  style={{
                                    border: `1px solid ${colors.border}`,
                                    backgroundColor: colors.bgSecondary,
                                    color: colors.textMuted,
                                  }}
                                >
                                  {s.label} (source pending)
                                </span>
                              );
                            })}
                          </div>
                        </td>
                      </tr>
                      {isOpen && (
                        <tr style={{ borderBottom: `1px solid ${colors.borderDark}`, backgroundColor: colors.bgTertiary }}>
                          <td colSpan={colCount} className="px-4 pb-5 pt-1">
                            <EvidenceDetails company={r} filters={toggles} />
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          {toggles.bds.enabled && results && results.length > 0 && (
            <p className="mt-3 text-xs leading-relaxed max-w-3xl" style={{ color: colors.textMuted }}>
              {LENS_LABELS.occupationResultsFootnote}
            </p>
          )}

          {results && results.length > 0 && (
            <div className="mt-4 flex justify-center">
              <button
                type="button"
                onClick={() => setShowLoginModal(true)}
                className="px-6 py-3 rounded-lg text-sm font-medium transition-all duration-200 hover:opacity-90"
                style={{
                  backgroundColor: colors.bgSecondary,
                  color: colors.textPrimary,
                  border: `1px solid ${colors.border}`,
                }}
              >
                Save Search
              </button>
            </div>
          )}
        </div>
        <LoginModal isOpen={showLoginModal} onClose={() => setShowLoginModal(false)} />
      </section>
    );
  }

  return (
    <section className="mt-9 mb-4">
      <div
        className="rounded-2xl p-[26px]"
        style={{
          border: `1px solid ${colors.border}`,
          backgroundColor: colors.bgSecondary,
          boxShadow: '0 24px 60px -28px rgba(0,0,0,0.6)',
        }}
      >
        <div className="mb-5 flex items-center justify-end">
          <div className="flex items-center gap-2">
            {backendStatus === 'checking' && (
              <div className="flex items-center gap-2 text-xs" style={{ color: colors.yellow }}>
                <Loader2 className="h-3 w-3 animate-spin" />
                Checking...
              </div>
            )}
            {backendStatus === 'connected' && (
              <div className="flex items-center gap-2 text-xs" style={{ color: colors.green }}>
                <CheckCircle2 className="h-3 w-3" />
                Connected
              </div>
            )}
            {backendStatus === 'disconnected' && (
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-2 text-xs" style={{ color: colors.red }}>
                  <XCircle className="h-3 w-3" />
                  Disconnected
                </div>
                <button
                  type="button"
                  onClick={checkBackendHealth}
                  className="text-xs underline hover:opacity-80 transition-opacity"
                  style={{ color: colors.blue }}
                >
                  Retry
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="mb-[22px]">
          <div className="text-[13px] font-semibold" style={{ color: colors.textSecondary }}>Quick presets</div>
          <div className="mt-2.5 flex flex-wrap gap-2">
            {SCREENING_PRESETS.map((preset) => {
              const isActive = activePreset === preset.id;
              return (
                <button
                  key={preset.id}
                  type="button"
                  title={preset.description}
                  onClick={() => applyPreset(preset.id)}
                  className="rounded-full px-3.5 py-1.5 text-[12.5px] font-semibold whitespace-nowrap transition-all duration-200"
                  style={{
                    border: `1px solid ${isActive ? colors.greenDim : colors.border}`,
                    backgroundColor: isActive ? `${colors.greenDim}29` : colors.bgTertiary,
                    color: isActive ? colors.green : colors.textSecondary,
                  }}
                >
                  {preset.label}
                </button>
              );
            })}
          </div>
          {activePreset && (
            <p className="mt-2.5 text-[12.5px] mb-0" style={{ color: colors.textMuted }}>
              {SCREENING_PRESETS.find((p) => p.id === activePreset)?.description}
            </p>
          )}
        </div>

        <form
          aria-label="Screening"
          className="grid gap-6 md:grid-cols-[1.7fr_1fr]"
          onSubmit={(e) => { e.preventDefault(); runDemo(); }}
        >
          <div>
            <div className="flex items-center justify-between gap-2">
              <label htmlFor="ticker-input" className="text-[13px] font-semibold" style={{ color: colors.textSecondary }}>
                Paste tickers <span className="font-normal" style={{ color: colors.textFaint }}>— comma, space, or newline</span>
              </label>
              <div className="flex items-center gap-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,text/csv"
                  className="hidden"
                  aria-hidden
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) void handleCsvUpload(file);
                  }}
                />
                <button
                  type="button"
                  disabled={isParsingCsv || isLoading}
                  onClick={() => fileInputRef.current?.click()}
                  className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1 text-xs font-semibold transition-opacity disabled:opacity-50 whitespace-nowrap"
                  style={{
                    border: `1px solid ${colors.border}`,
                    backgroundColor: colors.bgTertiary,
                    color: colors.textSecondary,
                  }}
                >
                  {isParsingCsv ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Upload className="h-3.5 w-3.5" />
                  )}
                  Upload CSV
                </button>
              </div>
            </div>
            <textarea
              id="ticker-input"
              placeholder="e.g., AAPL, MSFT, VOO&#10;TSLA&#10;VTI"
              className="mt-2.5 h-[150px] w-full resize-none rounded-xl px-4 py-3.5 text-sm font-mono leading-relaxed focus:outline-none placeholder:text-slate-500"
              style={{
                backgroundColor: colors.bgTertiary,
                border: `1px solid ${colors.border}`,
                color: colors.textPrimary,
              }}
              value={tickers}
              onChange={(e) => setTickers(e.target.value)}
              onFocus={(e) => { e.target.style.borderColor = colors.blue; }}
              onBlur={(e) => { e.target.style.borderColor = colors.border; }}
            />
            <p className="font-mono text-[11px] mt-2 mb-0" style={{ color: colors.textFaint }}>
              {tickerCount} symbol{tickerCount === 1 ? '' : 's'} · CSV with Market Value enables dollar-weighting + ETF X-Ray.
            </p>
          </div>

          <div>
            <div className="text-[13px] font-semibold" style={{ color: colors.textSecondary }}>Lenses</div>
            <div className="mt-2.5 flex flex-col gap-2">
              <ToggleRow
                label={LENS_LABELS.occupation}
                sub={LENS_LABELS.occupationSub}
                value={toggles.bds.enabled}
                onChange={() => setToggles(t => ({ ...t, bds: { ...t.bds, enabled: !t.bds.enabled } }))}
              />
              <ToggleRow
                label={LENS_LABELS.defense}
                sub="SIPRI Top-100 arms producers; DoD contracts when loaded"
                value={toggles.defense}
                onChange={() => setToggles(t => ({ ...t, defense: !t.defense }))}
              />
              <ToggleRow
                label={LENS_LABELS.shariah}
                sub="DJIM/S&P-style 33/33/49 + 5% interest/revenue + haram business"
                value={toggles.shariah}
                onChange={() => setToggles(t => ({ ...t, shariah: !t.shariah }))}
              />
            </div>
          </div>
        </form>

        <div className="mt-6 flex flex-wrap items-center justify-between gap-4">
          <button
            type="button"
            onClick={runDemo}
            disabled={!canRun}
            className="inline-flex items-center gap-2 rounded-xl px-[26px] py-[13px] text-[15px] font-bold transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
            style={{
              background: `linear-gradient(135deg, ${colors.green}, ${colors.greenDim})`,
              color: colors.ctaInk,
              boxShadow: '0 12px 28px -12px rgba(52,211,153,0.6)',
              border: 'none',
            }}
          >
            Run ethical check
            <ArrowRight className="h-4 w-4" strokeWidth={2.4} />
          </button>
          <div className="text-[12.5px] flex items-center gap-2" style={{ color: colors.textMuted }}>
            <Info className="h-4 w-4" />
            <span>We link to a public source for every verdict.</span>
          </div>
        </div>

        {csvWarnings.length > 0 && (
          <div
            className="mt-4 p-3 rounded-xl text-sm"
            style={{
              backgroundColor: `${colors.yellow}10`,
              border: `1px solid ${colors.yellow}30`,
              color: colors.yellow,
            }}
          >
            {csvWarnings.join(' ')}
          </div>
        )}

        {error && (
          <div
            className="mt-6 p-4 rounded-xl"
            style={{
              backgroundColor: `${colors.red}10`,
              border: `1px solid ${colors.red}30`,
            }}
          >
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-2 text-sm" style={{ color: colors.red }}>
                <AlertTriangle className="h-4 w-4" />
                {error}
              </div>
              {tickers.trim() && (
                <button
                  type="button"
                  onClick={runDemo}
                  className="text-xs font-medium underline"
                  style={{ color: colors.blue }}
                >
                  Retry
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

